import React, { useState, useEffect } from 'react';
import { X, Search, Plus, Heart, MessageCircle, Share2, Eye, Users, Calendar, Trash2 } from 'lucide-react';
import CreatePromoModal from './CreatePromoModal';
import './PromoSpot.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PromoFeedModal = ({ isOpen, onClose, user }) => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deletingPromoId, setDeletingPromoId] = useState(null);

  const genres = ['all', 'house', 'techno', 'drum & bass', 'dubstep', 'trance', 'hip-hop', 'trap', 'bass', 'deep house'];
  const categories = [
    { value: 'all', label: 'All Promos' },
    { value: 'mix_track', label: '🎵 Mixes & Tracks' },
    { value: 'event', label: '🎉 Events' },
    { value: 'general', label: '📢 Announcements' }
  ];

  useEffect(() => {
    if (isOpen) {
      fetchPromos();
    }
  }, [isOpen, searchTerm, selectedGenre, selectedCategory, sortBy]);

  // Listen for openPromoDetail event to open a specific promo
  useEffect(() => {
    const handleOpenPromoDetail = async (event) => {
      const { promoId } = event.detail || {};
      console.log('📢 PromoFeedModal received openPromoDetail event. promoId:', promoId, 'isOpen:', isOpen);
      if (promoId && isOpen) {
        // Find the promo in the current list
        const promo = promos.find(p => p.id === promoId);
        if (promo) {
          console.log('📢 Found promo in list, opening:', promo.title);
          setSelectedPromo(promo);
        } else {
          console.log('📢 Promo not in list, fetching from backend...');
          // If not in current list, fetch it from backend
          try {
            const response = await fetch(`${BACKEND_URL}/api/promos/${promoId}`);
            const data = await response.json();
            if (data.success) {
              console.log('📢 Fetched promo from backend:', data.promo.title);
              setSelectedPromo(data.promo);
            }
          } catch (error) {
            console.error('Failed to fetch promo:', error);
          }
        }
      }
    };

    window.addEventListener('openPromoDetail', handleOpenPromoDetail);
    return () => {
      window.removeEventListener('openPromoDetail', handleOpenPromoDetail);
    };
  }, [isOpen, promos]);

  const fetchPromos = async () => {
    setLoading(true);
    try {
      let url = `${BACKEND_URL}/api/promos?limit=20&sort=${sortBy}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      if (selectedGenre !== 'all') url += `&genre=${encodeURIComponent(selectedGenre)}`;
      if (selectedCategory !== 'all') url += `&category=${selectedCategory}`;

      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success) {
        setPromos(data.promos || []);
      }
    } catch (error) {
      console.error('Failed to fetch promos:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleLike = async (promoId) => {
    if (!user) {
      alert('Please login to like promos');
      return;
    }

    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/promos/${promoId}/like`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        // Update local state
        setPromos(prevPromos => 
          prevPromos.map(promo => 
            promo.id === promoId 
              ? { ...promo, likes_count: data.likes_count, user_liked: data.liked }
              : promo
          )
        );
      }
    } catch (error) {
      console.error('Failed to toggle like:', error);
    }
  };

  const sharePromo = async (promoId) => {
    try {
      const token = localStorage.getItem('beatseek_token');
      await fetch(`${BACKEND_URL}/api/promos/${promoId}/share`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Copy link to clipboard
      const shareLink = `${window.location.origin}/promo/${promoId}`;
      navigator.clipboard.writeText(shareLink);
      alert('Link copied to clipboard!');
    } catch (error) {
      console.error('Failed to share promo:', error);
    }
  };

  const deletePromo = async (promoId) => {
    if (!window.confirm('Are you sure you want to delete this promo? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingPromoId(promoId);
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/promos/${promoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        // Remove from local state
        setPromos(prevPromos => prevPromos.filter(promo => promo.id !== promoId));
        // Close detail modal if this promo was open
        if (selectedPromo?.id === promoId) {
          setSelectedPromo(null);
        }
      } else {
        alert(data.detail || 'Failed to delete promo');
      }
    } catch (error) {
      console.error('Failed to delete promo:', error);
      alert('Failed to delete promo. Please try again.');
    } finally {
      setDeletingPromoId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="promo-feed-modal-overlay">
      <div className="promo-feed-modal">
        {/* Header */}
        <div className="promo-feed-header">
          <div className="promo-feed-title">
            <span className="promo-megaphone">📢</span>
            <h2>The Promo Spot</h2>
          </div>
          <button className="promo-close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {/* Controls Bar */}
        <div className="promo-controls-bar">
          {/* Search */}
          <div className="promo-search-container">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search promos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="promo-search-input"
            />
          </div>

          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="promo-select"
          >
            {categories.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>

          {/* Genre Filter */}
          <select
            value={selectedGenre}
            onChange={(e) => setSelectedGenre(e.target.value)}
            className="promo-select"
          >
            <option value="all">All Genres</option>
            {genres.filter(g => g !== 'all').map(genre => (
              <option key={genre} value={genre}>
                {genre.charAt(0).toUpperCase() + genre.slice(1)}
              </option>
            ))}
          </select>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="promo-select"
          >
            <option value="newest">📅 Newest</option>
            <option value="featured">⭐ Featured</option>
            <option value="trending">🔥 Trending</option>
          </select>

          {/* Post Button */}
          {user && (
            <button
              className="promo-post-btn"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus size={18} />
              Post Promo
            </button>
          )}
        </div>

        {/* Promo Grid */}
        <div className="promo-grid">
          {loading ? (
            <div className="promo-loading">Loading promos...</div>
          ) : promos.length === 0 ? (
            <div className="promo-empty">
              <span className="empty-icon">📢</span>
              <p>No promos yet. Be the first to post!</p>
            </div>
          ) : (
            promos.map(promo => (
              <PromoCard
                key={promo.id}
                promo={promo}
                onLike={toggleLike}
                onShare={sharePromo}
                onDelete={deletePromo}
                onClick={() => setSelectedPromo(promo)}
                currentUser={user}
                isDeleting={deletingPromoId === promo.id}
              />
            ))
          )}
        </div>

        {/* Create Promo Modal */}
        {showCreateModal && (
          <CreatePromoModal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setShowCreateModal(false);
              fetchPromos();
            }}
            user={user}
          />
        )}

        {/* Detail Modal */}
        {selectedPromo && (
          <PromoDetailModal
            promo={selectedPromo}
            onClose={() => setSelectedPromo(null)}
            onLike={toggleLike}
            onDelete={deletePromo}
            currentUser={user}
          />
        )}
      </div>
    </div>
  );
};

// PromoCard Component
const PromoCard = ({ promo, onLike, onShare, onDelete, onClick, currentUser, isDeleting }) => {
  const isOwner = currentUser && promo.user_id === currentUser.id;
  
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'mix_track': return '🎵';
      case 'event': return '🎉';
      case 'general': return '📢';
      default: return '📢';
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(promo.id);
  };

  return (
    <div className={`promo-card ${promo.is_boosted ? 'boosted' : ''}`} onClick={onClick}>
      {promo.is_boosted && (
        <div className="boost-badge" title={`Boosted until ${new Date(promo.boost_end_date).toLocaleDateString()}`}>
          ⭐
        </div>
      )}

      {/* User Info */}
      <div className="promo-card-header">
        <div className="promo-user-info">
          <div className="promo-avatar">
            {promo.user_avatar ? (
              <img src={`${BACKEND_URL}${promo.user_avatar}`} alt={promo.user_name} />
            ) : (
              <span>{promo.user_name.charAt(0).toUpperCase()}</span>
            )}
          </div>
          <div className="promo-user-details">
            <span className="promo-user-name">{promo.user_name}</span>
            <span className="promo-time">{new Date(promo.created_at).toLocaleDateString()}</span>
          </div>
        </div>
        <span className="promo-category-icon">{getCategoryIcon(promo.category)}</span>
      </div>

      {/* Media Preview */}
      {promo.images && promo.images.length > 0 && (
        <div className="promo-media">
          <img src={`${BACKEND_URL}${promo.images[0]}`} alt={promo.title} />
        </div>
      )}

      {promo.video_url && (
        <div className="promo-media">
          <video src={`${BACKEND_URL}${promo.video_url}`} controls />
        </div>
      )}

      {/* Audio Preview for Mix/Track */}
      {promo.category === 'mix_track' && promo.audio_url && (
        <div className="promo-audio-preview">
          <audio src={`${BACKEND_URL}${promo.audio_url}`} controls />
          <span className="audio-label">🎧 Audio Preview</span>
        </div>
      )}

      {/* Content */}
      <div className="promo-content">
        {/* Linked Event Badge */}
        {promo.category === 'event' && promo.linked_event_name && (
          <div className="linked-event-badge">
            📅 Linked to: <strong>{promo.linked_event_name}</strong>
            {promo.linked_event_date && (
              <span className="event-date-mini"> • {new Date(promo.linked_event_date).toLocaleDateString()}</span>
            )}
          </div>
        )}
        
        {promo.title && <h3 className="promo-title">{promo.title}</h3>}
        {promo.description && (
          <p className="promo-description">
            {promo.description.length > 150 
              ? `${promo.description.substring(0, 150)}...` 
              : promo.description}
          </p>
        )}
        
        {/* Genre Tags */}
        {promo.genre_tags && promo.genre_tags.length > 0 && (
          <div className="promo-tags">
            {promo.genre_tags.slice(0, 3).map((tag, index) => (
              <span key={index} className="promo-tag">#{tag}</span>
            ))}
          </div>
        )}

        {/* External Links */}
        {promo.external_links && promo.external_links.length > 0 && (
          <div className="promo-links">
            {promo.external_links.slice(0, 2).map((link, index) => (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="promo-external-link"
                onClick={(e) => e.stopPropagation()}
              >
                🔗 {link.label}
              </a>
            ))}
          </div>
        )}
      </div>

      {/* Engagement */}
      <div className="promo-engagement" onClick={(e) => e.stopPropagation()}>
        <button className="engagement-btn" onClick={() => onLike(promo.id)}>
          <Heart size={18} fill={promo.user_liked ? '#ff3366' : 'none'} />
          <span>{promo.likes_count || 0}</span>
        </button>
        <button className="engagement-btn">
          <MessageCircle size={18} />
          <span>{promo.comments_count || 0}</span>
        </button>
        <button className="engagement-btn" onClick={() => onShare(promo.id)}>
          <Share2 size={18} />
          <span>{promo.shares_count || 0}</span>
        </button>
        <div className="engagement-views">
          <Eye size={16} />
          <span>{promo.views_count || 0}</span>
        </div>

        {/* Event-specific buttons */}
        {promo.category === 'event' && (
          <>
            <button className="engagement-btn interested-btn">
              <Users size={18} />
              <span>{promo.interested_count || 0} Interested</span>
            </button>
            <button className="engagement-btn attending-btn">
              <Calendar size={18} />
              <span>{promo.attending_count || 0} Attending</span>
            </button>
          </>
        )}
      </div>

      {/* Delete Link - Only visible to owner, at bottom */}
      {isOwner && (
        <div 
          style={{ 
            padding: '0.5rem 1rem', 
            borderTop: '1px solid rgba(255, 255, 255, 0.05)',
            textAlign: 'right'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#ff6b6b',
              fontSize: '0.8rem',
              cursor: isDeleting ? 'wait' : 'pointer',
              opacity: isDeleting ? 0.5 : 1,
              padding: '4px 0'
            }}
          >
            {isDeleting ? 'Removing...' : 'Remove'}
          </button>
        </div>
      )}
    </div>
  );
};

// Promo Detail Modal
const PromoDetailModal = ({ promo, onClose, onLike, onDelete, currentUser }) => {
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isOwner = currentUser && promo.user_id === currentUser.id;

  useEffect(() => {
    if (promo) {
      fetchComments();
    }
  }, [promo]);

  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/promos/${promo.id}/comments`);
      const data = await response.json();
      if (data.success) {
        setComments(data.comments || []);
      }
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    await onDelete(promo.id);
    setIsDeleting(false);
  };

  const handleAddComment = async () => {
    if (!currentUser || !commentText.trim()) return;

    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/promos/${promo.id}/comment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ comment_text: commentText })
      });

      const data = await response.json();
      if (data.success) {
        setComments([data.comment, ...comments]);
        setCommentText('');
      }
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'mix_track': return '🎵';
      case 'event': return '🎉';
      case 'general': return '📢';
      default: return '📢';
    }
  };

  return (
    <div className="promo-detail-overlay" onClick={onClose}>
      <div className="promo-detail-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="promo-detail-header">
          <div className="promo-detail-user">
            <div className="promo-avatar">
              {promo.user_avatar ? (
                <img src={`${BACKEND_URL}${promo.user_avatar}`} alt={promo.user_name} />
              ) : (
                <span>{promo.user_name.charAt(0).toUpperCase()}</span>
              )}
            </div>
            <div>
              <div className="promo-user-name">{promo.user_name}</div>
              <div className="promo-time">{new Date(promo.created_at).toLocaleString()}</div>
            </div>
            <span className="promo-category-badge">
              {getCategoryIcon(promo.category)} {promo.category.replace('_', ' ').toUpperCase()}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {/* Delete Link - Only visible to owner */}
            {isOwner && (
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#ff6b6b',
                  fontSize: '0.85rem',
                  cursor: isDeleting ? 'wait' : 'pointer',
                  opacity: isDeleting ? 0.5 : 1,
                  padding: '4px 0'
                }}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
            <button className="promo-close-btn" onClick={onClose}>
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="promo-detail-content">
          {/* Boost Badge */}
          {promo.is_boosted && (
            <div className="boost-banner">
              ⭐ Boosted Post - Featured until {new Date(promo.boost_end_date).toLocaleDateString()}
            </div>
          )}

          {/* Title */}
          {promo.title && <h2 className="promo-detail-title">{promo.title}</h2>}

          {/* Description */}
          {promo.description && (
            <p className="promo-detail-description">{promo.description}</p>
          )}

          {/* Media */}
          {promo.images && promo.images.length > 0 && (
            <div className="promo-detail-images">
              {promo.images.map((img, index) => (
                <img key={index} src={`${BACKEND_URL}${img}`} alt={`Image ${index + 1}`} />
              ))}
            </div>
          )}

          {promo.video_url && (
            <div className="promo-detail-video">
              <video src={`${BACKEND_URL}${promo.video_url}`} controls />
            </div>
          )}

          {promo.category === 'mix_track' && promo.audio_url && (
            <div className="promo-detail-audio">
              <audio src={`${BACKEND_URL}${promo.audio_url}`} controls />
              <span className="audio-label">🎧 Audio Preview</span>
            </div>
          )}

          {/* Genre Tags */}
          {promo.genre_tags && promo.genre_tags.length > 0 && (
            <div className="promo-tags">
              {promo.genre_tags.map((tag, index) => (
                <span key={index} className="promo-tag">#{tag}</span>
              ))}
            </div>
          )}

          {/* External Links */}
          {promo.external_links && promo.external_links.length > 0 && (
            <div className="promo-external-links">
              <h4>Links:</h4>
              {promo.external_links.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="promo-external-link-btn"
                >
                  🔗 {link.label || link.type}
                </a>
              ))}
            </div>
          )}

          {/* Event Info with Navigation Choice */}
          {promo.category === 'event' && promo.linked_event_name && (
            <div className="promo-event-info">
              <h4>📅 Linked Event:</h4>
              <p><strong>{promo.linked_event_name}</strong></p>
              {promo.linked_event_date && (
                <p>{new Date(promo.linked_event_date).toLocaleString()}</p>
              )}
              
              <div style={{ marginTop: '15px', textAlign: 'center' }}>
                <button
                  style={{
                    background: 'linear-gradient(135deg, #ffd700, #ffed4e)',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '12px 20px',
                    color: '#1a1a2e',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    width: '100%'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Trigger navigation to event calendar
                    const eventNavEvent = new CustomEvent('openEventCalendar', { 
                      detail: { 
                        eventId: promo.linked_event_id,
                        eventDate: promo.linked_event_date
                      } 
                    });
                    window.dispatchEvent(eventNavEvent);
                    
                    // Close promo spot
                    onClose();
                  }}
                >
                  📅 View on Event Calendar
                </button>
              </div>
            </div>
          )}

          {/* Engagement Stats */}
          <div className="promo-detail-engagement">
            <button className="engagement-btn" onClick={() => onLike(promo.id)}>
              <Heart size={20} fill={promo.user_liked ? '#ff3366' : 'none'} />
              <span>{promo.likes_count || 0} Likes</span>
            </button>
            <div className="engagement-stat">
              <MessageCircle size={20} />
              <span>{promo.comments_count || 0} Comments</span>
            </div>
            <div className="engagement-stat">
              <Share2 size={20} />
              <span>{promo.shares_count || 0} Shares</span>
            </div>
            <div className="engagement-stat">
              <Eye size={20} />
              <span>{promo.views_count || 0} Views</span>
            </div>
          </div>

          {/* Comments Section */}
          <div className="promo-comments-section">
            <h4>Comments</h4>
            
            {/* Add Comment */}
            {currentUser && (
              <div className="add-comment">
                <input
                  type="text"
                  placeholder="Add a comment..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                />
                <button onClick={handleAddComment}>Post</button>
              </div>
            )}

            {/* Comments List */}
            <div className="comments-list">
              {loadingComments ? (
                <p>Loading comments...</p>
              ) : comments.length === 0 ? (
                <p className="no-comments">No comments yet. Be the first!</p>
              ) : (
                comments.map((comment) => (
                  <div key={comment.id} className="comment">
                    <div className="comment-avatar">
                      {comment.user_avatar ? (
                        <img src={`${BACKEND_URL}${comment.user_avatar}`} alt={comment.user_name} />
                      ) : (
                        <span>{comment.user_name.charAt(0).toUpperCase()}</span>
                      )}
                    </div>
                    <div className="comment-content">
                      <div className="comment-header">
                        <span className="comment-user">{comment.user_name}</span>
                        <span className="comment-time">{new Date(comment.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className="comment-text">{comment.comment_text}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoFeedModal;
